/**
 * Все форматы дат приложения — порт `RuDates` Android-версии.
 *
 * Двадцать один форматтер был объявлен в тринадцати файлах, и русская локаль строилась
 * двумя разными способами. Здесь формат один на всё приложение, и поменять вид даты
 * значит поправить одну строку.
 *
 * `Intl` в родительном падеже («14 сентября», а не «14 сентябрь») даёт только формат
 * с днём и месяцем вместе — поэтому месяцы перечислены явно: собирать строку из
 * `Intl.DateTimeFormat` по частям и получить «сентябрь» вместо «сентября» очень легко.
 */

const MONTHS = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

const MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'мая',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];

const two = (value: number) => String(value).padStart(2, '0');

/** «14 сентября 2026» — там, где год важен: день рождения, далёкий срок. */
export const full = (date: Date) =>
  `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;

/** «14 сентября» — в пределах текущего года. */
export const dayMonth = (date: Date) => `${date.getDate()} ${MONTHS[date.getMonth()]}`;

/** «14 сен» — в тесной строке списка. */
export const shortDayMonth = (date: Date) => `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`;

/** «14.09.2026» — рядом с размером файла и прочими цифрами. */
export const numeric = (date: Date) =>
  `${two(date.getDate())}.${two(date.getMonth() + 1)}.${date.getFullYear()}`;

export const time = (date: Date) => `${two(date.getHours())}:${two(date.getMinutes())}`;

/** «14 сентября, 10:30». */
export const dayMonthTime = (date: Date) => `${dayMonth(date)}, ${time(date)}`;

/** «14 сен, 10:30» — для строки списка. */
export const shortDayMonthTime = (date: Date) => `${shortDayMonth(date)}, ${time(date)}`;

/** «14 сентября 2026, 10:30». */
export const fullDateTime = (date: Date) => `${full(date)}, ${time(date)}`;

/**
 * Русские окончания при числе: 1 дело, 2 дела, 5 дел, 11 дел, 21 дело.
 *
 * Считать это приходится и делам, и документам, и клиентам, а две копии правила
 * рано или поздно разойдутся на числах вроде 111.
 */
export function plural(count: number, one: string, few: string, many: string): string {
  return `${count} ${pluralWord(count, one, few, many)}`;
}

export function pluralWord(count: number, one: string, few: string, many: string): string {
  // 11–14 — исключение: «одиннадцать дел», хотя последняя цифра говорит иначе.
  const teens = count % 100;
  if (teens >= 11 && teens <= 14) return many;

  const last = count % 10;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

/** Месяцы в именительном падеже: «Сентябрь 2026» — заголовок, а не дата. */
const MONTHS_NOMINATIVE = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

/** Дни недели с понедельника: календарь у нас начинается с него, а не с воскресенья. */
export const WEEKDAYS_SHORT = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

const WEEKDAYS = [
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
  'воскресенье',
];

/** «Сентябрь 2026» — шапка месяца в календаре. */
export const monthYear = (date: Date) =>
  `${MONTHS_NOMINATIVE[date.getMonth()]} ${date.getFullYear()}`;

/** «14 сентября, понедельник» — заголовок выбранного дня. */
export const dayMonthWeekday = (date: Date) =>
  `${dayMonth(date)}, ${WEEKDAYS[weekdayIndex(date)]}`;

/** Номер дня недели, где понедельник — 0: `getDay()` считает от воскресенья. */
export const weekdayIndex = (date: Date) => (date.getDay() + 6) % 7;
